import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, RefreshCw } from "lucide-react";

const STATUS_META = {
  pending: { fr: "Action requise", en: "Action required", color: "bg-amber-100 text-amber-800 border-amber-300" },
  confirmed: { fr: "Réinscription confirmée", en: "Re-enrollment confirmed", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  not_reenrolling: { fr: "Ne se réinscrit pas", en: "Not re-enrolling", color: "bg-rose-100 text-rose-800 border-rose-300" },
  hold: { fr: "En réflexion", en: "On hold", color: "bg-orange-100 text-orange-800 border-orange-300" },
  enrolled: { fr: "Inscription payée", en: "Enrolled", color: "bg-sky-100 text-sky-800 border-sky-300" },
};

export default function ReenrollmentCard() {
  const { lang } = useLang();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reasonOpen, setReasonOpen] = useState(null);  // { id, response }
  const [reason, setReason] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const reload = () => setRefreshTick(t => t + 1);

  useEffect(() => {
    let cancelled = false;
    const fetchData = () => {
      setLoading(true);
      api.get("/parent/reenrollments")
        .then(r => { if (!cancelled) setItems(r.data); })
        .catch(() => {})
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    // Defer to next microtask so React Compiler doesn't flag setState-in-effect
    queueMicrotask(fetchData);
    return () => { cancelled = true; };
  }, [refreshTick]);

  const respond = (id, response, reasonText = null) => {
    api.post(`/parent/reenrollments/${id}/respond`, { response, reason: reasonText })
      .then(() => {
        trackEvent("reenrollment_response", { response });
        toast.success(lang === "fr" ? "Merci pour votre retour" : "Thanks for your response");
        setReasonOpen(null);
        setReason("");
        reload();
      })
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };

  const startEnrollment = (it) => {
    api.post(`/parent/reenrollments/${it.id}/start-enrollment`)
      .then(r => nav(`/parent/enrollment/${r.data.id}`))
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };

  if (loading || items.length === 0) return null;

  return (
    <>
      {items.map(it => {
        const meta = STATUS_META[it.status] || STATUS_META.pending;
        return (
          <Card key={it.id} className="mb-4 border-accent/40 bg-gradient-to-br from-accent/[0.04] to-transparent" data-testid={`reenroll-card-${it.id}`}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <Badge className={`${meta.color} text-[10px] mb-2`}>
                    <RefreshCw className="w-3 h-3 mr-1 inline" /> {lang === "fr" ? "Réinscription" : "Re-enrollment"} · {it.school_year}
                  </Badge>
                  <h3 className="font-serif text-xl tracking-tight">
                    {lang === "fr"
                      ? `${it.student_first_name} souhaite-t-il/elle continuer à OFA ?`
                      : `Will ${it.student_first_name} continue at OFA?`}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {lang === "fr"
                      ? `Classe actuelle : ${it.current_grade} · Classe suivante : `
                      : `Current grade: ${it.current_grade} · Next grade: `}
                    <strong>{it.next_grade}</strong>
                  </p>
                </div>
                <Badge variant="outline" className={`${meta.color}`}>{meta[lang]}</Badge>
              </div>

              {it.status === "pending" && (
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button
                    size="sm"
                    onClick={() => respond(it.id, "confirmed")}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`reenroll-yes-${it.id}`}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1.5" />
                    {lang === "fr" ? "Oui, je réinscris" : "Yes, re-enroll"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setReasonOpen({ id: it.id, response: "hold" }); setReason(""); }}
                    data-testid={`reenroll-hold-${it.id}`}
                  >
                    <Clock className="w-4 h-4 mr-1.5" />
                    {lang === "fr" ? "J'hésite encore" : "I'm still thinking"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setReasonOpen({ id: it.id, response: "not_reenrolling" }); setReason(""); }}
                    className="text-destructive hover:text-destructive"
                    data-testid={`reenroll-no-${it.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1.5" />
                    {lang === "fr" ? "Non" : "No"}
                  </Button>
                </div>
              )}

              {it.status === "confirmed" && !it.enrollment_id && (
                <div className="pt-2 border-t border-border space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {lang === "fr"
                      ? "Étape suivante : choisissez votre plan de paiement et réglez l'acompte (295 € pour 1 cours, 390 € pour 2 cours)."
                      : "Next step: choose your payment plan and pay the deposit (€295 for 1 course, €390 for 2 courses)."}
                  </p>
                  <Button size="sm" onClick={() => startEnrollment(it)} data-testid={`reenroll-start-${it.id}`}>
                    {lang === "fr" ? "Choisir mon plan & payer l'acompte" : "Choose plan & pay deposit"}
                  </Button>
                </div>
              )}

              {it.status === "confirmed" && it.enrollment_id && (
                <Button size="sm" onClick={() => nav(`/parent/enrollment/${it.enrollment_id}`)} data-testid={`reenroll-resume-${it.id}`}>
                  {lang === "fr" ? "Reprendre la réinscription" : "Resume re-enrollment"}
                </Button>
              )}

              {it.status === "enrolled" && (
                <div className="pt-2 border-t border-border space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {lang === "fr"
                      ? `Réinscription validée pour l'année ${it.school_year}. Acompte payé · solde annuel à régler selon votre plan.`
                      : `Re-enrollment validated for ${it.school_year}. Deposit paid · annual balance follows your plan.`}
                  </p>
                  <Button size="sm" onClick={() => nav(`/parent/payment`)} data-testid={`reenroll-view-plan-${it.id}`}>
                    {lang === "fr" ? "Voir mon plan & échéances" : "View my plan & schedule"}
                  </Button>
                </div>
              )}

              {it.status === "hold" && (
                <div className="text-sm text-muted-foreground pt-2 border-t border-border">
                  {lang === "fr"
                    ? "Vous nous direz quand vous serez prêt. Vous pouvez aussi confirmer dès maintenant :"
                    : "Let us know when you're ready. You can also confirm now:"}
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" onClick={() => respond(it.id, "confirmed")} data-testid={`reenroll-hold-yes-${it.id}`}>
                      {lang === "fr" ? "Confirmer la réinscription" : "Confirm re-enrollment"}
                    </Button>
                  </div>
                </div>
              )}

              {it.status === "not_reenrolling" && (
                <p className="text-sm text-muted-foreground pt-2 border-t border-border">
                  {lang === "fr"
                    ? "Nous avons bien noté votre choix. Merci pour la confiance que vous nous avez accordée."
                    : "Your decision has been recorded. Thank you for the trust you placed in us."}
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Reason dialog (hold or not_reenrolling) */}
      <Dialog open={!!reasonOpen} onOpenChange={(v) => !v && setReasonOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {reasonOpen?.response === "not_reenrolling"
                ? (lang === "fr" ? "Nous sommes désolés de vous voir partir" : "We're sorry to see you go")
                : (lang === "fr" ? "Une question, une hésitation ?" : "Any question or hesitation?")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {lang === "fr"
                ? "Pouvez-vous nous dire pourquoi en quelques mots ? (optionnel)"
                : "Could you tell us why in a few words? (optional)"}
            </p>
            <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} data-testid="reason-textarea" />
            <Button
              onClick={() => respond(reasonOpen.id, reasonOpen.response, reason || null)}
              className="w-full"
              data-testid="reason-confirm-btn"
            >
              {lang === "fr" ? "Envoyer" : "Send"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
